import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { DOCUMENT } from '@angular/common';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly LANGUAGE_KEY = 'atalef_language';
  private readonly DEFAULT_LANGUAGE = 'en';
  private readonly RTL_LANGUAGES = new Set(['he', 'ar', 'fa', 'ur']);
  
  // Available languages - can be updated from API
  private defaultLanguages: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' }
  ];

  private availableLanguagesSubject = new BehaviorSubject<Language[]>(this.defaultLanguages);
  public availableLanguages$ = this.availableLanguagesSubject.asObservable();

  private currentLanguageSubject = new BehaviorSubject<string>(this.DEFAULT_LANGUAGE);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private apiService: ApiService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initializeLanguage();
    this.loadAvailableLanguages();
  }

  private initializeLanguage(): void {
    const savedLanguage = this.getSavedLanguage();
    const languageToUse = savedLanguage || this.DEFAULT_LANGUAGE;
    this.setLanguage(languageToUse, false).catch(error => {
      console.error('Failed to initialize language', error);
    });
  }

  /**
   * Load language from user claims after authentication
   * Should be called by UserProxy after user is loaded
   */
  public loadLanguageFromClaims(claims: any): void {
    if (claims && claims['UI.Language']) {
      const claimLanguage = claims['UI.Language'];
      const currentLanguage = this.getCurrentLanguage();
      
      // Only update if different from current language
      if (claimLanguage !== currentLanguage) {
        console.log(`Loading language from claim: ${claimLanguage}`);
        this.setLanguage(claimLanguage, false).catch(error => {
          console.error('Failed to load language from claim', error);
        });
      }
    }
  }

  public getSavedLanguage(): string | null {
    return localStorage.getItem(this.LANGUAGE_KEY);
  }

  public async setLanguage(languageCode: string, saveToServer: boolean = true): Promise<void> {
    let languageToUse = languageCode;

    if (!this.isLanguageAvailable(languageToUse)) {
      console.warn(`Language ${languageToUse} is not available. Using default.`);
      languageToUse = this.DEFAULT_LANGUAGE;
    }

    localStorage.setItem(this.LANGUAGE_KEY, languageToUse);
    this.currentLanguageSubject.next(languageToUse);
    this.updateDocumentDirection(languageToUse);

    try {
      await firstValueFrom(this.translate.use(languageToUse));
      
      // Save to configuration API if requested (user changed language)
      if (saveToServer) {
        this.saveLanguageToConfiguration(languageToUse);
      }
    } catch (error) {
      console.error(`Failed to apply translations for language ${languageToUse}`, error);
    }
  }

  public getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  public isLanguageAvailable(languageCode: string): boolean {
    return this.availableLanguagesSubject.value.some(lang => lang.code === languageCode);
  }

  public getAvailableLanguages(): Language[] {
    return this.availableLanguagesSubject.value;
  }

  public getLanguageName(languageCode: string): string {
    const language = this.availableLanguagesSubject.value.find(lang => lang.code === languageCode);
    return language ? language.name : languageCode;
  }

  public getLanguageNativeName(languageCode: string): string {
    const language = this.availableLanguagesSubject.value.find(lang => lang.code === languageCode);
    return language ? language.nativeName : languageCode;
  }

  private loadAvailableLanguages(): void {
    this.apiService.getAvailableLanguages().subscribe(
      (languages: any[]) => {
        if (languages && languages.length > 0) {
          // Map API response (PascalCase) to our interface (camelCase)
          const mappedLanguages = languages.map(lang => ({
            code: lang.Code || lang.code,
            name: lang.Name || lang.name,
            nativeName: lang.NativeName || lang.nativeName
          }));
          this.availableLanguagesSubject.next(mappedLanguages);
          console.log('Loaded available languages from API:', mappedLanguages);
        }
      },
      error => {
        console.warn('Failed to load available languages from API, using defaults', error);
        // Keep the default languages already set in BehaviorSubject
      }
    );
  }

  private updateDocumentDirection(languageCode: string): void {
    if (!this.document?.body) {
      return;
    }

    const isRtl = this.RTL_LANGUAGES.has(languageCode.toLowerCase());
    const dirValue = isRtl ? 'rtl' : 'ltr';

    this.document.body.setAttribute('dir', dirValue);
    this.document.body.classList.remove('rtl', 'ltr');
    this.document.body.classList.add(dirValue);

    if (this.document.documentElement) {
      this.document.documentElement.setAttribute('dir', dirValue);
      this.document.documentElement.classList.remove('rtl', 'ltr');
      this.document.documentElement.classList.add(dirValue);
    }
  }

  /**
   * Save language preference to configuration API
   * Uses POST /config/language endpoint
   */
  private saveLanguageToConfiguration(languageCode: string): void {
    this.apiService.saveUserLanguage(languageCode).subscribe(
      () => {
        console.log(`Language preference saved to configuration: ${languageCode}`);
      },
      (error) => {
        console.error('Failed to save language preference to configuration', error);
      }
    );
  }
}
